import { useEffect, useState, useMemo, useRef } from "react";
import api, { formatError, mediaUrl } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Check, Plus, Trash2, Pencil, X, Save, Upload, Image as ImageIcon, MessageCircle, CornerDownRight, Shield, Send, RefreshCw } from "lucide-react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

export default function Admin() {
  const [pending, setPending] = useState(0);

  const refreshPending = async () => {
    try {
      const { data } = await api.get("/admin/comments/pending-count");
      setPending(data.count || 0);
      window.dispatchEvent(new CustomEvent("comments-pending", { detail: data.count || 0 }));
    } catch { /* ignore */ }
  };

  useEffect(() => {
    refreshPending();
    const id = setInterval(refreshPending, 30000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      <div className="mb-8">
        <div className="font-mono text-xs uppercase tracking-widest text-teal-700 mb-2">Control room</div>
        <h1 className="font-serif font-bold text-3xl sm:text-4xl tracking-tight text-slate-900">Admin panel</h1>
        <p className="mt-2 text-slate-500 text-sm">Set the correct answers for both questionnaires and manage the blog.</p>
      </div>

      <Tabs defaultValue="knowledge">
        <TabsList className="mb-6">
          <TabsTrigger value="knowledge" data-testid="admin-tab-knowledge">Knowledge Answers</TabsTrigger>
          <TabsTrigger value="checklist" data-testid="admin-tab-checklist">Checklist Answers</TabsTrigger>
          <TabsTrigger value="external" data-testid="admin-tab-external">External Ratings</TabsTrigger>
          <TabsTrigger value="knowledge-top100" data-testid="admin-tab-knowledge-top100">Knowledge Participants</TabsTrigger>
          <TabsTrigger value="blog" data-testid="admin-tab-blog">Blog Posts</TabsTrigger>
          <TabsTrigger value="comments" data-testid="admin-tab-comments" className="relative">
            Comments
            {pending > 0 && (
              <span data-testid="admin-comments-badge" className="ml-2 inline-grid place-items-center min-w-[20px] h-5 px-1.5 rounded-full bg-rose-500 text-white text-[11px] font-bold animate-pulse">
                {pending}
              </span>
            )}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="knowledge"><KnowledgeAnswers /></TabsContent>
        <TabsContent value="checklist"><ChecklistAnswers /></TabsContent>
        <TabsContent value="external"><ExternalAssessments /></TabsContent>
        <TabsContent value="knowledge-top100"><KnowledgeParticipants /></TabsContent>
        <TabsContent value="blog"><BlogManager /></TabsContent>
        <TabsContent value="comments"><CommentsModerator onChange={refreshPending} /></TabsContent>
      </Tabs>
    </div>
  );
}

function CommentsModerator({ onChange }) {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [replyBody, setReplyBody] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async (st = status) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/comments?status=${st}`);
      setItems(data.comments || []);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(status); /* eslint-disable-next-line */ }, [status]);

  const approve = async (id) => {
    try {
      await api.put(`/admin/comments/${id}/approve`);
      toast.success("Approved — now visible to users");
      await load();
      onChange && onChange();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this comment and all of its replies?")) return;
    try {
      await api.delete(`/admin/comments/${id}`);
      toast.success("Deleted");
      await load();
      onChange && onChange();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const sendReply = async (c) => {
    if (!replyBody.trim() || busy) return;
    setBusy(true);
    try {
      await api.post(`/quiz/${c.quiz}/questions/${c.question_id}/comments`, {
        body: replyBody.trim(),
        parent_id: c.id,
      });
      toast.success("Reply posted");
      setReplyTo(null);
      setReplyBody("");
      await load();
      onChange && onChange();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const TABS = [
    { key: "pending", label: "Awaiting approval" },
    { key: "approved", label: "Approved" },
    { key: "all", label: "All" },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-slate-500">Review questions & comments left on the Knowledge test explanations. Approve to make them visible, reply, or delete.</p>
        <button onClick={() => load()} data-testid="comments-refresh" className="inline-flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-900">
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      <div className="flex gap-1.5">
        {TABS.map((tb) => (
          <button
            key={tb.key}
            data-testid={`comments-filter-${tb.key}`}
            onClick={() => setStatus(tb.key)}
            className={`h-8 px-3 rounded-lg text-xs font-medium border transition-all ${status === tb.key ? "border-teal-500 bg-teal-600 text-white" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}
          >
            {tb.label}
          </button>
        ))}
      </div>

      {loading && <p className="text-sm text-slate-400 font-mono">Loading…</p>}
      {!loading && items.length === 0 && (
        <p className="text-sm text-slate-400 italic" data-testid="comments-empty">No comments here.</p>
      )}

      <div className="space-y-3">
        {items.map((c) => (
          <div key={c.id} data-testid={`admin-comment-${c.id}`} className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <span className="font-mono text-[10px] uppercase tracking-widest text-teal-700 bg-teal-50 border border-teal-100 rounded px-2 py-0.5">
                Q{c.question_order}
              </span>
              <span className="text-xs text-slate-500 truncate max-w-[60%]">{c.question_text}</span>
              {c.status === "pending" ? (
                <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-100 rounded px-2 py-0.5">Pending</span>
              ) : (
                <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-100 rounded px-2 py-0.5">Approved</span>
              )}
            </div>

            {c.parent_snippet && (
              <div className="mb-2 pl-3 border-l-2 border-slate-200 text-xs text-slate-400">
                <CornerDownRight size={11} className="inline mr-1" />
                reply to <span className="font-medium text-slate-500">{c.parent_author}</span>: “{c.parent_snippet}”
              </div>
            )}

            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-semibold text-slate-800">{c.author_name}</span>
              {c.is_admin && (
                <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-teal-700 bg-teal-50 border border-teal-100 rounded px-1.5 py-0.5">
                  <Shield size={10} /> Admin
                </span>
              )}
              <span className="text-[11px] text-slate-400">{c.created_at ? new Date(c.created_at).toLocaleString() : ""}</span>
            </div>
            <p className="text-sm text-slate-700 whitespace-pre-wrap break-words">{c.body}</p>

            <div className="flex items-center gap-3 mt-3">
              {c.status === "pending" && (
                <button onClick={() => approve(c.id)} data-testid={`comment-approve-${c.id}`} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium transition-colors">
                  <Check size={13} /> Approve
                </button>
              )}
              <button onClick={() => { setReplyTo(replyTo === c.id ? null : c.id); setReplyBody(""); }} data-testid={`comment-reply-${c.id}`} className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-teal-700">
                <Send size={13} /> Reply
              </button>
              <button onClick={() => del(c.id)} data-testid={`comment-delete-${c.id}`} className="inline-flex items-center gap-1.5 text-xs text-rose-500 hover:text-rose-700">
                <Trash2 size={13} /> Delete
              </button>
            </div>

            {replyTo === c.id && (
              <div className="mt-3">
                <Textarea
                  data-testid={`comment-reply-input-${c.id}`}
                  rows={2}
                  value={replyBody}
                  onChange={(e) => setReplyBody(e.target.value)}
                  placeholder="Write a reply (posted publicly as Admin)…"
                  className="text-sm"
                />
                <div className="flex items-center gap-2 mt-1.5">
                  <Button size="sm" onClick={() => sendReply(c)} disabled={busy || !replyBody.trim()} data-testid={`comment-reply-send-${c.id}`} className="h-8 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs">
                    {busy ? "Posting…" : "Post reply"}
                  </Button>
                  <button onClick={() => setReplyTo(null)} className="text-xs text-slate-500 hover:text-slate-700">Cancel</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ExplanationField({ q, onSave, testid, showImage }) {
  const [en, setEn] = useState(q.explanation || "");
  const [nl, setNl] = useState(q.explanation_nl || "");
  const [vid, setVid] = useState(q.video_url || "");
  const [uploadingImg, setUploadingImg] = useState(false);
  useEffect(() => { setEn(q.explanation || ""); setNl(q.explanation_nl || ""); setVid(q.video_url || ""); }, [q.id, q.explanation, q.explanation_nl, q.video_url]);

  const uploadExplImage = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setUploadingImg(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.url) { onSave(q.id, { explanation_image: data.url }); toast.success("Image uploaded"); }
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setUploadingImg(false);
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-slate-100 space-y-3">
      <div className="grid md:grid-cols-2 gap-3">
      <div>
        <Label className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-1 block">Explanation (EN) — why this is the right answer</Label>
        <Textarea
          data-testid={testid}
          rows={2}
          value={en}
          onChange={(e) => setEn(e.target.value)}
          onBlur={() => { if (en !== (q.explanation || "")) onSave(q.id, { explanation: en }); }}
          placeholder="Explain why the correct answer is right (shown to registered users after they finish the test)…"
          className="text-sm"
        />
      </div>
      <div>
        <Label className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-1 block">Uitleg (NL) — waarom dit het juiste antwoord is</Label>
        <Textarea
          data-testid={`${testid}-nl`}
          rows={2}
          value={nl}
          onChange={(e) => setNl(e.target.value)}
          onBlur={() => { if (nl !== (q.explanation_nl || "")) onSave(q.id, { explanation_nl: nl }); }}
          placeholder="Leg uit waarom het juiste antwoord klopt (getoond aan geregistreerde gebruikers na afloop van de test)…"
          className="text-sm"
        />
      </div>
      </div>
      <div>
        <Label className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-1 block">YouTube video (optional, shared EN/NL)</Label>
        <Input
          data-testid={`${testid}-video`}
          value={vid}
          onChange={(e) => setVid(e.target.value)}
          onBlur={() => { if (vid !== (q.video_url || "")) onSave(q.id, { video_url: vid }); }}
          placeholder="https://www.youtube.com/watch?v=…"
          className="text-sm"
        />
      </div>
      {showImage && (
        <div>
          <Label className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-1 block">Explanation image (optional, shown above the explanation)</Label>
          <div className="flex items-start gap-3">
            <div className="w-32 h-24 rounded-lg border border-slate-200 bg-slate-50 overflow-hidden shrink-0 grid place-items-center">
              {q.explanation_image ? (
                <img src={mediaUrl(q.explanation_image)} alt="explanation" data-testid={`${testid}-image-preview`} className="w-full h-full object-cover" />
              ) : (
                <ImageIcon size={22} className="text-slate-300" />
              )}
            </div>
            <div className="flex flex-col gap-2">
              <label className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-slate-300 text-sm text-slate-700 hover:border-slate-400 cursor-pointer w-fit">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp"
                  data-testid={`${testid}-image-input`}
                  className="hidden"
                  onChange={(e) => uploadExplImage(e.target.files?.[0])}
                />
                <Upload size={15} /> {uploadingImg ? "Uploading…" : "Upload image"}
              </label>
              {q.explanation_image && (
                <button
                  type="button"
                  data-testid={`${testid}-image-clear`}
                  onClick={() => onSave(q.id, { explanation_image: "" })}
                  className="text-xs text-rose-600 hover:underline w-fit"
                >
                  Remove image
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ExternalAssessments() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ trainer_name: "", country: "", correct_prob: 50, rated_by: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/external-assessments").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const add = async () => {
    if (!form.trainer_name.trim()) { toast.error("Trainer name is required"); return; }
    if (!form.rated_by.trim()) { toast.error("'Rated by' is required"); return; }
    setBusy(true);
    try {
      await api.post("/admin/external-assessment", {
        trainer_name: form.trainer_name,
        country: form.country,
        correct_prob: Math.max(1, Math.min(99, Number(form.correct_prob) || 0)),
        rated_by: form.rated_by,
      });
      toast.success("External rating added to the Top 100");
      setForm({ trainer_name: "", country: "", correct_prob: 50, rated_by: "" });
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/admin/external-assessment/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-8" data-testid="external-assessments">
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <h3 className="font-serif font-bold text-xl text-slate-900">Add an external assessment</h3>
        <p className="text-sm text-slate-500 mt-1">Manually place a trainer on the Checklist Top 100 with a verdict from an external source.</p>
        <div className="grid sm:grid-cols-2 gap-4 mt-5">
          <div>
            <Label className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-1 block">Name</Label>
            <Input data-testid="ext-name" value={form.trainer_name} onChange={(e) => set("trainer_name", e.target.value)} placeholder="Trainer name" />
          </div>
          <div>
            <Label className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-1 block">Country</Label>
            <Input data-testid="ext-country" value={form.country} onChange={(e) => set("country", e.target.value)} placeholder="e.g. NL" />
          </div>
          <div>
            <Label className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-1 block">Probability correct (1–99%)</Label>
            <Input data-testid="ext-prob" type="number" min={1} max={99} value={form.correct_prob} onChange={(e) => set("correct_prob", e.target.value)} />
          </div>
          <div>
            <Label className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-1 block">Rated by</Label>
            <Input data-testid="ext-ratedby" value={form.rated_by} onChange={(e) => set("rated_by", e.target.value)} placeholder="Who gave this verdict" />
          </div>
        </div>
        <Button onClick={add} disabled={busy} data-testid="ext-add-btn" className="mt-5 bg-slate-900 hover:bg-slate-800 text-white rounded-full">
          <Plus size={15} className="mr-2" /> {busy ? "Adding…" : "Add to Top 100"}
        </Button>
      </div>

      <div>
        <h4 className="font-mono text-xs uppercase tracking-widest text-slate-400 mb-3">Existing external assessments ({items.length})</h4>
        {items.length === 0 ? (
          <p className="text-sm text-slate-400">None yet.</p>
        ) : (
          <div className="space-y-2">
            {items.map((it) => (
              <div key={it.id} data-testid={`ext-row-${it.id}`} className="flex items-center gap-4 bg-white border border-slate-200 rounded-lg p-3">
                <div className="w-14 text-center font-serif font-bold text-lg text-slate-900">{it.correct_prob}%</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{it.trainer_name}</div>
                  <div className="text-xs text-slate-400 truncate">{it.country ? it.country + " · " : ""}Rated by: {it.rated_by}</div>
                </div>
                <button onClick={() => remove(it.id)} data-testid={`ext-delete-${it.id}`} className="text-rose-600 hover:text-rose-700 p-2">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function OrphanLinker({ orphan, onAssigned }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    const h = setTimeout(() => {
      api.get(`/admin/users?search=${encodeURIComponent(query)}`)
        .then((r) => { if (active) setResults(r.data); })
        .finally(() => { if (active) setLoading(false); });
    }, 250);
    return () => { active = false; clearTimeout(h); };
  }, [open, query]);

  const assign = async (u) => {
    if (u.has_knowledge_result) { toast.error("This user already has a result — cannot overwrite"); return; }
    if (!window.confirm(`Link this result (score ${orphan.score}) to ${u.display_name} (${u.email})?`)) return;
    try {
      await api.post(`/admin/knowledge-orphans/${orphan.id}/assign`, { user_id: u.id });
      toast.success("Linked to " + u.display_name);
      onAssigned();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid={`orphan-${orphan.id}`} className="bg-white border border-amber-200 rounded-lg p-3">
      <div className="flex items-center gap-4">
        <div className="w-14 text-center font-serif font-bold text-lg text-slate-900">{orphan.score}</div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-900 truncate">{orphan.display_name}</div>
          <div className="text-xs text-slate-400">anoniem · {orphan.date ? new Date(orphan.date).toLocaleDateString() : ""}</div>
        </div>
        <Button size="sm" variant={open ? "secondary" : "default"} data-testid={`orphan-link-btn-${orphan.id}`} onClick={() => setOpen((v) => !v)} className="rounded-full">
          {open ? "Sluiten" : "Koppel aan gebruiker"}
        </Button>
      </div>
      {open && (
        <div className="mt-3 border-t border-slate-100 pt-3">
          <Input data-testid={`orphan-search-${orphan.id}`} autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Zoek geregistreerde gebruiker op naam of e-mail…" className="text-sm" />
          <div className="mt-2 max-h-56 overflow-auto space-y-1">
            {loading && <p className="text-xs text-slate-400 font-mono px-1">Laden…</p>}
            {!loading && results.length === 0 && <p className="text-xs text-slate-400 px-1">Geen gebruikers gevonden.</p>}
            {results.map((u) => (
              <button
                key={u.id}
                data-testid={`orphan-user-${orphan.id}-${u.id}`}
                disabled={u.has_knowledge_result}
                onClick={() => assign(u)}
                className={`w-full text-left flex items-center gap-2 px-2.5 py-2 rounded-md border text-sm transition-colors ${u.has_knowledge_result ? "border-slate-100 text-slate-300 cursor-not-allowed" : "border-slate-200 hover:border-teal-400 hover:bg-teal-50 text-slate-700"}`}
              >
                <span className="font-medium truncate">{u.display_name}</span>
                <span className="text-xs text-slate-400 truncate">{u.email}</span>
                {u.has_knowledge_result && <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-slate-400">heeft al resultaat</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function KnowledgeParticipants() {
  const [items, setItems] = useState([]);
  const [orphans, setOrphans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/knowledge-participants"),
      api.get("/admin/knowledge-orphans"),
    ]).then(([p, o]) => { setItems(p.data); setOrphans(o.data); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  const remove = async (p) => {
    const who = p.email || p.display_name;
    if (!window.confirm(`Remove ${who} from the Knowledge Top 100? This deletes their test result.`)) return;
    try {
      await api.delete(`/admin/knowledge-participant/${encodeURIComponent(p.user_id)}`);
      toast.success("Removed from the leaderboard");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const levelOf = (s) => (s >= 80 ? "Master Trainer" : s >= 55 ? "Trainer" : s >= 45 ? "Master Practitioner" : s >= 26 ? "Practitioner" : "Novice");

  const q = query.trim().toLowerCase();
  const filtered = q ? items.filter((p) => (p.display_name || "").toLowerCase().includes(q) || (p.email || "").toLowerCase().includes(q)) : items;

  return (
    <div className="space-y-6" data-testid="knowledge-participants">
      <div>
        <h3 className="font-serif font-bold text-xl text-slate-900">Knowledge Test participants</h3>
        <p className="text-sm text-slate-500 mt-1">Registered users who completed the Knowledge Test, ranked by score. Remove anyone to take them off the Top 100 (this deletes their result).</p>
      </div>

      {!loading && orphans.length > 0 && (
        <div className="bg-amber-50/60 border border-amber-200 rounded-xl p-4" data-testid="orphans-panel">
          <h4 className="font-semibold text-slate-900">Anonieme resultaten nog niet gekoppeld ({orphans.length})</h4>
          <p className="text-xs text-slate-500 mt-1 mb-3">Resultaten van mensen die de test anoniem maakten. Koppel elk resultaat aan de juiste geregistreerde gebruiker om ze op de Top 100 te zetten.</p>
          <div className="space-y-2">
            {orphans.map((o) => (
              <OrphanLinker key={o.id} orphan={o} onAssigned={load} />
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-3">
        <Input data-testid="kp-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name or email…" className="max-w-sm" />
        <span className="text-xs font-mono uppercase tracking-widest text-slate-400">{filtered.length} / {items.length}</span>
      </div>

      {loading ? (
        <p className="text-sm text-slate-400 font-mono">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-slate-400" data-testid="kp-empty">No participants yet.</p>
      ) : (
        <div className="space-y-2">
          {filtered.map((p) => (
            <div key={p.user_id} data-testid={`kp-row-${p.user_id}`} className="flex items-center gap-4 bg-white border border-slate-200 rounded-lg p-3">
              <div className="w-8 text-center font-mono text-sm text-slate-400">#{p.rank}</div>
              <div className="w-14 text-center font-serif font-bold text-lg text-slate-900">{p.score}</div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 truncate flex items-center gap-2">
                  {p.display_name}
                  {p.is_manual && <span className="text-[10px] font-mono uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-100 rounded px-1.5 py-0.5">manual</span>}
                </div>
                <div className="text-xs text-slate-400 truncate">
                  {p.email ? p.email + " · " : ""}NLP {levelOf(p.score)} niveau · {p.date ? new Date(p.date).toLocaleDateString() : ""}
                </div>
              </div>
              <button onClick={() => remove(p)} data-testid={`kp-delete-${p.user_id}`} className="text-rose-600 hover:text-rose-700 p-2" title="Remove from leaderboard">
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function KnowledgeAnswers() {
  const [questions, setQuestions] = useState([]);
  useEffect(() => { api.get("/admin/quiz/knowledge").then((r) => setQuestions(r.data.questions)); }, []);

  const setCorrect = async (qid, idx) => {
    setQuestions((qs) => qs.map((q) => (q.id === qid ? { ...q, correct: idx } : q)));
    try {
      await api.put(`/admin/question/${qid}`, { correct: idx });
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  const saveExpl = async (qid, patch) => {
    setQuestions((qs) => qs.map((q) => (q.id === qid ? { ...q, ...patch } : q)));
    try {
      await api.put(`/admin/question/${qid}`, patch);
      toast.success("Explanation saved");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-500 mb-4">Tick the box beside the correct option and add an explanation of why it is right. Changes save automatically. Scores update for all future submissions.</p>
      {questions.map((q, i) => (
        <div key={q.id} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`admin-knowledge-q-${i}`}>
          <div className="flex items-baseline gap-2 mb-3">
            <span className="font-mono text-xs text-slate-400">{i + 1}</span>
            <p className="text-sm font-medium text-slate-800">{q.text}</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            {q.options.map((opt, oi) => {
              const isCorrect = q.correct === oi;
              return (
                <button
                  key={oi}
                  data-testid={`admin-correct-answer-checkbox-${i}-${oi}`}
                  onClick={() => setCorrect(q.id, oi)}
                  className={`flex items-center gap-3 text-left rounded-lg border p-2.5 text-sm transition-all ${isCorrect ? "border-teal-500 bg-teal-50" : "border-slate-200 hover:border-slate-300"}`}
                >
                  <span className={`grid place-items-center w-5 h-5 rounded border shrink-0 ${isCorrect ? "bg-teal-600 border-teal-600 text-white" : "border-slate-300"}`}>
                    {isCorrect && <Check size={13} />}
                  </span>
                  <span className={isCorrect ? "text-slate-900 font-medium" : "text-slate-600"}>{opt}</span>
                </button>
              );
            })}
          </div>
          <ExplanationField q={q} onSave={saveExpl} testid={`admin-knowledge-explanation-${i}`} showImage />
        </div>
      ))}
    </div>
  );
}

function ChecklistAnswers() {
  const [questions, setQuestions] = useState([]);
  useEffect(() => { api.get("/admin/quiz/checklist").then((r) => setQuestions(r.data.questions)); }, []);

  const setIdeal = async (qid, ideal) => {
    setQuestions((qs) => qs.map((q) => (q.id === qid ? { ...q, ideal } : q)));
    try {
      await api.put(`/admin/question/${qid}`, { ideal });
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  const saveExpl = async (qid, patch) => {
    setQuestions((qs) => qs.map((q) => (q.id === qid ? { ...q, ...patch } : q)));
    try {
      await api.put(`/admin/question/${qid}`, patch);
      toast.success("Explanation saved");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-2">
      <p className="text-sm text-slate-500 mb-4">Choose the answer a genuine, competent trainer would give. Matching answers raise the trainer's reliability score.</p>
      {questions.map((q, i) => (
        <div key={q.id} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`admin-checklist-q-${i}`}>
          <div className="flex items-center gap-4">
            <div className="flex items-baseline gap-2 flex-1 min-w-0">
              <span className="font-mono text-xs text-slate-400">{i + 1}</span>
              <p className="text-sm text-slate-800 truncate">{q.text}</p>
            </div>
            <div className="flex gap-1.5 shrink-0">
              {["Yes", "No", "Unknown"].map((opt) => (
                <button
                  key={opt}
                  data-testid={`admin-checklist-ideal-${i}-${opt.toLowerCase()}`}
                  onClick={() => setIdeal(q.id, opt)}
                  className={`h-8 px-3 rounded-lg text-xs font-medium border transition-all ${q.ideal === opt ? "border-teal-500 bg-teal-600 text-white" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
          <ExplanationField q={q} onSave={saveExpl} testid={`admin-checklist-explanation-${i}`} />
        </div>
      ))}
    </div>
  );
}

const EMPTY = { title: "", excerpt: "", content: "", category: "General", cover_image: "", published: true, title_nl: "", excerpt_nl: "", content_nl: "", category_nl: "" };

const QUILL_TOOLBAR = [
  [{ header: [2, 3, false] }],
  ["bold", "italic", "underline", "strike", "blockquote"],
  [{ list: "ordered" }, { list: "bullet" }],
  ["link", "image"],
  [{ align: [] }],
  ["clean"],
];

function BlogManager() {
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState(null); // null = list, {} = editing
  const [busy, setBusy] = useState(false);
  const [editLang, setEditLang] = useState("en");
  const [uploading, setUploading] = useState(false);
  const quillRef = useRef(null);

  const uploadImage = async (file) => {
    if (!file) return null;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return null;
    }
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/admin/upload-image", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data.url;
  };

  const uploadCover = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      if (url) {
        set("cover_image", url);
        toast.success("Cover image uploaded");
      }
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const imageHandler = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/png,image/jpeg,image/gif,image/webp";
    input.click();
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const editor = quillRef.current?.getEditor();
      const range = editor?.getSelection(true);
      const index = range ? range.index : editor?.getLength() || 0;
      editor?.insertText(index, "Uploading image…", { italic: true });
      try {
        const url = await uploadImage(file);
        editor?.deleteText(index, "Uploading image…".length);
        if (url) {
          editor?.insertEmbed(index, "image", url, "user");
          editor?.setSelection(index + 1);
          toast.success("Image inserted");
        }
      } catch (e) {
        editor?.deleteText(index, "Uploading image…".length);
        toast.error(formatError(e.response?.data?.detail));
      }
    };
  };

  const quillModules = useMemo(
    () => ({ toolbar: { container: QUILL_TOOLBAR, handlers: { image: imageHandler } } }),
    []
  );

  const load = () => api.get("/admin/blog").then((r) => setPosts(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      if (form.id) await api.put(`/blog/${form.id}`, form);
      else await api.post("/blog", form);
      toast.success("Saved");
      setForm(null);
      await load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try { await api.delete(`/blog/${id}`); await load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  if (form) {
    const nl = editLang === "nl";
    const f = (name) => (nl ? `${name}_nl` : name);
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-serif font-bold text-xl text-slate-900">{form.id ? "Edit post" : "New post"}</h3>
          <Button variant="ghost" size="sm" onClick={() => setForm(null)}><X size={16} /></Button>
        </div>

        {/* Language switch for the content fields */}
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono uppercase tracking-widest text-slate-400">Language</span>
          <div className="flex rounded-full border border-slate-200 overflow-hidden text-xs font-mono">
            {["en", "nl"].map((l) => (
              <button
                key={l}
                type="button"
                data-testid={`blog-lang-${l}`}
                onClick={() => setEditLang(l)}
                className={`px-3 py-1 uppercase tracking-widest transition-colors ${editLang === l ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}
              >
                {l === "en" ? "English" : "Nederlands"}
              </button>
            ))}
          </div>
          <span className="text-xs text-slate-400">{nl ? "Editing the Dutch version" : "Editing the English version"}</span>
        </div>

        <Field label={nl ? "Titel (NL)" : "Title (EN)"}>
          <Input data-testid="blog-title" value={form[f("title")] || ""} onChange={(e) => set(f("title"), e.target.value)} />
        </Field>
        <Field label={nl ? "Categorie (NL)" : "Category (EN)"}>
          <Input data-testid="blog-category" value={form[f("category")] || ""} onChange={(e) => set(f("category"), e.target.value)} />
        </Field>
        <Field label="Cover image (shared)">
          <div className="flex flex-col sm:flex-row gap-4 items-start">
            <div className="w-40 h-28 rounded-lg border border-slate-200 bg-slate-50 overflow-hidden shrink-0 grid place-items-center">
              {form.cover_image ? (
                <img src={mediaUrl(form.cover_image)} alt="cover preview" data-testid="blog-cover-preview" className="w-full h-full object-cover" />
              ) : (
                <ImageIcon size={22} className="text-slate-300" />
              )}
            </div>
            <div className="flex-1 space-y-2 w-full">
              <label>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp"
                  className="hidden"
                  data-testid="blog-cover-file"
                  onChange={(e) => uploadCover(e.target.files?.[0])}
                />
                <span className="inline-flex items-center gap-2 cursor-pointer rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
                  <Upload size={15} /> {uploading ? "Uploading…" : "Upload image"}
                </span>
              </label>
              <Input
                data-testid="blog-cover"
                placeholder="…or paste an image URL"
                value={form.cover_image || ""}
                onChange={(e) => set("cover_image", e.target.value)}
              />
              {form.cover_image && (
                <button type="button" onClick={() => set("cover_image", "")} data-testid="blog-cover-clear" className="text-xs text-rose-600 hover:underline">
                  Remove cover
                </button>
              )}
            </div>
          </div>
        </Field>
        <Field label={nl ? "Samenvatting (NL)" : "Excerpt (EN)"}>
          <Textarea data-testid="blog-excerpt" rows={2} value={form[f("excerpt")] || ""} onChange={(e) => set(f("excerpt"), e.target.value)} />
        </Field>
        <Field label={nl ? "Inhoud (NL)" : "Content (EN)"}>
          <div className="bg-white rounded-md">
            <ReactQuill
              key={editLang}
              ref={quillRef}
              theme="snow"
              modules={quillModules}
              value={form[f("content")] || ""}
              onChange={(html) => set(f("content"), html)}
              className="quill-editor"
            />
          </div>
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => setForm(null)} className="rounded-full">Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="blog-save-btn" className="bg-slate-900 hover:bg-slate-800 text-white rounded-full">
            <Save size={15} className="mr-2" /> {busy ? "Saving…" : "Save post"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <p className="text-sm text-slate-500">{posts.length} posts</p>
        <Button onClick={() => { setEditLang("en"); setForm({ ...EMPTY }); }} data-testid="admin-create-blog-btn" className="bg-slate-900 hover:bg-slate-800 text-white rounded-full">
          <Plus size={16} className="mr-1.5" /> New post
        </Button>
      </div>
      <div className="space-y-2">
        {posts.map((p) => (
          <div key={p.id} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-4" data-testid={`admin-blog-row-${p.slug}`}>
            <img src={mediaUrl(p.cover_image) || "https://images.unsplash.com/photo-1516110833967-0b5716ca1387?w=200&q=60"} alt="" className="w-16 h-12 rounded object-cover bg-slate-100 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-slate-900 truncate">{p.title}</div>
              <div className="text-xs text-slate-400">
                {p.category} · {new Date(p.created_at).toLocaleDateString()}
                {p.title_nl ? <span className="ml-2 text-teal-600">· NL ✓</span> : <span className="ml-2 text-amber-500">· NL missing</span>}
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => { setEditLang("en"); setForm(p); }} data-testid={`edit-blog-${p.slug}`}><Pencil size={14} /></Button>
            <Button variant="outline" size="sm" onClick={() => remove(p.id)} data-testid={`delete-blog-${p.slug}`} className="text-rose-600 hover:text-rose-700"><Trash2 size={14} /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
